/**
 * Редактор «Повернення постачальнику» (2026-07-29, ревізія меню Покупки):
 * повноцінна сторінка замість Drawer+read-only Viewer — дзеркало
 * GoodsReturnFromCustomerEditor (ч.225).
 *
 * - «На підставі»: попап ПРОВЕДЕНИХ приходів постачальника → рядки
 *   заповнюються ЗАЛИШКОМ до повернення (прийнято − вже повернуто).
 * - Частковий возврат: кількість рядка редагується (max = залишок).
 * - «+ Створити РК до вхідної ПН» — коригування податкового кредиту
 *   (перенесено з Viewer).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  goodsReturnsToSupplier, type GRTSItem,
} from '@/api/goodsReturns';
import { goodsReceipts } from '@/api/goodsReceipts';
import { taxInvoicesIncoming } from '@/api/taxInvoices';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import SearchSelect from '@/components/SearchSelect';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import { fmtDocDate, moneyDocActions, vatTail } from '@/shared/utils/paymentFromDoc';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { copyRailAction, nowForDocDate, useDocCopy } from '@/shared/hooks/useDocCopy';
import TchTable from '@/shared/components/TchTable';

interface ReturnableLine {
  nomenclature_id: string;
  characteristic_id: string | null;
  series_id: string | null;
  quantity_sold: string;
  quantity_returned: string;
  quantity_left: string;
  price: string;
  vat_rate_id: string;
}

interface Row extends Omit<GRTSItem, 'id'> {
  max_qty?: number;
}

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  contractor_id: string;
  base_receipt_id: string;
  warehouse_id: string;
  comment: string;
  items: Row[];
}

const EMPTY: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  contractor_id: '',
  base_receipt_id: '',
  warehouse_id: '',
  comment: '',
  items: [],
};

const fmt = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** ч.863: іконка документа для кнопки «заповнити з приходу». */
function DocIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden
      style={{ flex: '0 0 auto' }}>
      <path d="M9.5 1.5H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5l-3.5-3.5Z"
        stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M9.5 1.5V5H13" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}

export default function GoodsReturnToSupplierEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const {
    availableOrgs, hasPermissionInAnyOrg, totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('purchases.return.manage');
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const nomList = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });
  const vatList = useQuery({ queryKey: ['vat-rates'], queryFn: refs.vatRates });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [receiptPickerOpen, setReceiptPickerOpen] = useState(false);
  // РК до вхідної ПН (перенесено з Viewer).
  const [rkOpen, setRkOpen] = useState(false);
  const [rkInvoiceId, setRkInvoiceId] = useState('');
  const [rkNumber, setRkNumber] = useState('');
  const [rkDate, setRkDate] = useState(new Date().toISOString().slice(0, 10));
  const [rkRegistered, setRkRegistered] = useState(true);

  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refs.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });

  const numberPreview = useNumberPreview(
    'doc.goods_returns_to_supplier', form.organization_id, isNew);

  const docQuery = useQuery({
    queryKey: ['grts', id],
    queryFn: () => goodsReturnsToSupplier.get(id!),
    enabled: !isNew,
  });
  // ч.1268: «скопіювати» — зразок для нового документа (…/new?copy=<id>)
  const copy = useDocCopy(isNew, 'grts', (cid) => goodsReturnsToSupplier.get(cid));

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });

  // Проведені приходи постачальника — підстава повернення.
  const receiptsQ = useQuery({
    queryKey: ['receipts-for-return', form.organization_id, form.contractor_id],
    queryFn: () => goodsReceipts.list({
      organization_id: form.organization_id || undefined,
      contractor_id: form.contractor_id || undefined,
      posted: true, limit: 200,
    }),
    enabled: !!form.contractor_id,
  });

  // ?basis=<receipt_id> — кнопка «створити повернення» з прихода (2026-07-29).
  const basisApplied = useRef(false);
  const basisId = searchParams.get('basis');
  useEffect(() => {
    if (!isNew || !basisId || basisApplied.current) return;
    if (!vatList.data) return;
    basisApplied.current = true;
    void applyFromReceipt(basisId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, basisId, vatList.data]);

  useEffect(() => {
    if (isNew && !copy.source) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : { ...f, organization_id: firstOrg });
    } else if (copy.source ?? docQuery.data) {
      const d = (copy.source ?? docQuery.data)!;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        contractor_id: d.contractor_id,
        base_receipt_id: d.base_receipt_id ?? '',
        warehouse_id: d.warehouse_id ?? '',
        comment: d.comment ?? '',
        items: d.items.map((it) => ({
          line_number: it.line_number,
          nomenclature_id: it.nomenclature_id,
          characteristic_id: it.characteristic_id,
          series_id: it.series_id ?? null,
          quantity: String(it.quantity),
          price: String(it.price),
          amount_without_vat: String(it.amount_without_vat),
          vat_rate_id: it.vat_rate_id,
          vat_amount: String(it.vat_amount),
          total_amount: String(it.total_amount),
        })),
      });
      if (copy.source) {
        // копія — новий документ: свій номер і дата, без привʼязок зразка
        setForm((f) => ({ ...f, document_number: EMPTY.document_number, document_date: nowForDocDate(), }));
        copy.done();
      }
    }
  }, [isNew, docQuery.data, copy.source, availableOrgs]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);
  // Вкладки як у поверненні від покупця (рішення власника 2026-08-09).
  const [viewTab, setViewTab] = useState<'lines' | 'accounts' | 'tax'>('lines');

  const vatPercentById = useMemo(() => {
    const m = new Map<string, number>();
    for (const v of vatList.data ?? []) m.set(v.id, Number(v.rate_percent));
    return m;
  }, [vatList.data]);

  // Заповнити рядки ЗАЛИШКОМ до повернення обраного приходу.
  async function applyFromReceipt(receiptId: string) {
    try {
      const lines: ReturnableLine[] =
        await goodsReturnsToSupplier.returnable(receiptId);
      const receipt = await goodsReceipts.get(receiptId);
      const rows: Row[] = lines
        .filter((l) => Number(l.quantity_left) > 0)
        .map((l, idx) => {
          const qty = Number(l.quantity_left);
          const price = Number(l.price);
          const awv = qty * price;
          const rate = vatPercentById.get(l.vat_rate_id) ?? 0;
          const vat = awv * rate / 100;
          return {
            line_number: idx + 1,
            nomenclature_id: l.nomenclature_id,
            characteristic_id: l.characteristic_id,
            series_id: l.series_id,
            quantity: String(qty),
            price: String(price),
            amount_without_vat: awv.toFixed(2),
            vat_rate_id: l.vat_rate_id,
            vat_amount: vat.toFixed(2),
            total_amount: (awv + vat).toFixed(2),
            max_qty: qty,
          };
        });
      if (rows.length === 0) {
        setError('За цим приходом все вже повернуто.');
        setReceiptPickerOpen(false);
        return;
      }
      setForm((f) => ({
        ...f,
        organization_id: receipt.organization_id,
        contractor_id: receipt.contractor_id,
        base_receipt_id: receiptId,
        warehouse_id: f.warehouse_id || receipt.warehouse_id || '',
        items: rows,
      }));
      setError(null);
      setReceiptPickerOpen(false);
    } catch (e) {
      setError(extractError(e));
    }
  }

  function setQty(i: number, qty: string) {
    setForm((f) => ({
      ...f,
      items: f.items.map((row, idx) => {
        if (idx !== i) return row;
        const q = Number(qty) || 0;
        const price = Number(row.price);
        const awv = q * price;
        const rate = vatPercentById.get(row.vat_rate_id) ?? 0;
        const vat = awv * rate / 100;
        return {
          ...row, quantity: qty,
          amount_without_vat: awv.toFixed(2),
          vat_amount: vat.toFixed(2),
          total_amount: (awv + vat).toFixed(2),
        };
      }),
    }));
  }
  function setRate(i: number, vatRateId: string) {
    setForm((f) => ({
      ...f,
      items: f.items.map((row, idx) => {
        if (idx !== i) return row;
        const awv = Number(row.amount_without_vat);
        const rate = vatPercentById.get(vatRateId) ?? 0;
        const vat = awv * rate / 100;
        return { ...row, vat_rate_id: vatRateId,
          vat_amount: vat.toFixed(2), total_amount: (awv + vat).toFixed(2) };
      }),
    }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i)
        .map((row, idx) => ({ ...row, line_number: idx + 1 })),
    }));
  }

  function payloadItems() {
    return form.items
      .filter((it) => Number(it.quantity) > 0)
      .map((it, idx) => ({
        line_number: idx + 1,
        nomenclature_id: it.nomenclature_id,
        characteristic_id: it.characteristic_id,
        series_id: it.series_id,
        quantity: it.quantity,
        price: it.price,
        amount_without_vat: it.amount_without_vat,
        vat_rate_id: it.vat_rate_id,
        vat_amount: it.vat_amount,
        total_amount: it.total_amount,
        account_code: it.account_code ?? null,
      }));
  }
  function validate(): boolean {
    if (!form.base_receipt_id) {
      setError('Оберіть прихід-підставу («заповнити з приходу»).');
      return false;
    }
    if (payloadItems().length === 0) {
      setError('Немає рядків з кількістю > 0.');
      return false;
    }
    const over = form.items.find((it) =>
      it.max_qty != null && Number(it.quantity) > it.max_qty);
    if (over) {
      const nm = nomList.data?.items.find((n) => n.id === over.nomenclature_id)?.name;
      setError(`«${nm ?? 'Рядок'}»: кількість більша за залишок до повернення (${over.max_qty}).`);
      return false;
    }
    return true;
  }

  function createPayload() {
    return {
      organization_id: form.organization_id,
      document_number: form.document_number || null,
      document_date: new Date(form.document_date).toISOString(),
      base_receipt_id: form.base_receipt_id,
      warehouse_id: form.warehouse_id || null,
      comment: form.comment || null,
      items: payloadItems(),
    };
  }
  function updatePayload() {
    return {
      document_number: form.document_number || undefined,
      document_date: new Date(form.document_date).toISOString(),
      comment: form.comment || null,
      items: payloadItems(),
    };
  }

  const create = useMutation({
    mutationFn: () => goodsReturnsToSupplier.createFromReceipt(createPayload()),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['grts-list'] });
      navigate(`/goods-returns-to-supplier/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => goodsReturnsToSupplier.update(id!, updatePayload()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['grts', id] }),
    onError: (e) => setError(extractError(e)),
  });
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await goodsReturnsToSupplier.createFromReceipt(createPayload());
      try {
        await goodsReturnsToSupplier.post(doc.id);
      } catch (e) {
        // ч.1343: документ уже створено — відкриваємо його, інакше повторне
        // «провести» створило б дубль (правило ч.1266).
        navigate(`/goods-returns-to-supplier/${doc.id}`, { replace: true });
        throw e;
      }
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['grts-list'] });
      navigate(`/goods-returns-to-supplier/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await goodsReturnsToSupplier.update(id!, updatePayload());
      await goodsReturnsToSupplier.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['grts', id] });
      qc.invalidateQueries({ queryKey: ['grts-list'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => goodsReturnsToSupplier.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['grts', id] });
      qc.invalidateQueries({ queryKey: ['grts-list'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => goodsReturnsToSupplier.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['grts-list'] });
      navigate('/goods-returns-to-supplier');
    },
  });

  // РК до вхідної ПН: список проведених вхідних ПН постачальника.
  const invoicesQ = useQuery({
    queryKey: ['incoming-invoices-for-rk', form.contractor_id],
    queryFn: () => taxInvoicesIncoming.list({
      contractor_id: form.contractor_id, posted: true, limit: 200,
    }),
    enabled: rkOpen && !!form.contractor_id,
  });
  const createRk = useMutation({
    mutationFn: () => taxInvoicesIncoming.createCorrectionFromReturn({
      organization_id: form.organization_id,
      return_id: id!,
      corrected_invoice_id: rkInvoiceId,
      invoice_number: rkNumber,
      invoice_date: rkDate,
      is_registered_erpn: rkRegistered,
      registration_date: rkRegistered ? rkDate : undefined,
    }),
    onSuccess: (rk) => {
      setRkOpen(false);
      navigate(`/tax-invoices-incoming/${rk.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });

  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <p style={{ padding: '1rem' }}>
          У вас немає права створювати повернення у жодній з {totalAccessibleOrgs} організацій.
        </p>
      </div>
    );
  }

  const totalNoVat = form.items.reduce((s, it) => s + Number(it.amount_without_vat || 0), 0);
  const totalVat = form.items.reduce((s, it) => s + Number(it.vat_amount || 0), 0);
  const total = totalNoVat + totalVat;

  // ч.863: «ще дії» → права колонка «Повʼязані дії»: РК до вхідної ПН +
  // надходження/ПКО від постачальника (moneyDocActions) — обидві збережено.
  const railActions = [{
    label: 'створити РК до вхідної ПН',
    title: isNew ? 'Спершу збережіть документ'
      : 'Скласти розрахунок коригування до вхідної ПН на суму цього повернення',
    disabled: isNew,
    onClick: () => setRkOpen(true),
  },
  // ч.740: повернення коштів від постачальника — надходження / ПКО (Кт 631).
  ...moneyDocActions({ navigate, isNew, direction: 'in', prefill: {
    org: form.organization_id, contractor: form.contractor_id,
    contract: (form as { contract_id?: string }).contract_id ?? null,
    op: 'OTHER', corr: '631', amount: total,
    purpose: `Повернення коштів за повернутий товар згідно з накладною № ${form.document_number} від ${fmtDocDate(form.document_date)}${vatTail(totalVat)}`,
  } })];
  // ч.863: прогрес — ЛИШЕ обовʼязкові поля (підстава-прихід обовʼязкова).
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'party', title: 'Постачальник і підстава', required: 3,
      filled: (form.contractor_id ? 1 : 0) + (form.warehouse_id ? 1 : 0) + (form.base_receipt_id ? 1 : 0) },
    { id: 'items', title: 'Товари', required: 1, filled: form.items.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нове повернення постачальнику' : `Повернення: ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
          {/* 2026-08-06 (рішення власника): РК доступний і БЕЗ проводки —
              рядки повернення вже є, сам РК проводиться окремо. */}
          <DocumentToolbar
            isNew={isNew}
            isPosted={isPosted}
            onBackToList={() => navigate('/goods-returns-to-supplier')}
            onSave={() => { if (validate()) (isNew ? create.mutate() : update.mutate()); }}
            // Повернення без приходу-підстави не існує (base_receipt_id обовʼязковий).
            saveDisabled={!form.base_receipt_id}
            savePending={create.isPending || update.isPending}
            onPost={() => { if (validate()) (isNew ? createAndPost.mutate() : updateAndPost.mutate()); }}
            postPending={createAndPost.isPending || updateAndPost.isPending}
            postDisabled={!form.base_receipt_id || form.items.length === 0}
            onUnpost={() => unpost.mutate()}
            unpostPending={unpost.isPending}
            onDelete={() => { confirm('Видалити повернення?').then((ok) => { if (ok) remove.mutate(); }); }}
            deletePending={remove.isPending}
          onPrint={() => navigate(`/goods-returns-to-supplier/${id}/print`)}
            entriesDoc={{ type: 'doc.goods_returns_to_supplier', id: isNew ? '' : id! }}
            railMode
            extraActions={railActions}
          />
        </div>
      </div>

      {/* ч.863 (новий дизайн, Закон #10): блоки + права рейка. */}
      <DocLayout rail={
        <DocRail blocks={blocks} actions={[...railActions, copyRailAction(isNew, () => navigate(`/goods-returns-to-supplier/new?copy=${id}`))]} isNew={isNew}
          doc={{ type: 'doc.goods_returns_to_supplier', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Організація *</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value, base_receipt_id: '', items: [] })}
                  disabled={!isNew}>
            <option value="">— оберіть юрособу —</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>№ документа</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 disabled={isPosted}
                 placeholder={isNew ? (numberPreview ?? 'auto') : undefined} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Дата *</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })}
                 disabled={isPosted} />
        </div>
      </div>

      </DocBlock>

      <DocBlock index={2} title="Постачальник і підстава">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 5' }}>
          <label>Постачальник *</label>
          <SearchSelect
            items={(contractorsQ.data?.items ?? []).map((c) => ({
              id: c.id, label: c.name, hint: c.edrpou ?? null,
            }))}
            value={form.contractor_id}
            onChange={(cid) => setForm({ ...form, contractor_id: cid, base_receipt_id: '', items: [] })}
            disabled={isPosted || !isNew || !!form.base_receipt_id}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 5' }}>
          <label>Склад *{' '}
            <small style={{ color: 'var(--muted)' }}>(з якого повертається товар)</small>
          </label>
          <select value={form.warehouse_id}
                  onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })}
                  disabled={isPosted}>
            <option value="">— оберіть —</option>
            {warehousesQ.data?.map((w) => (
              <option key={w.id} value={w.id}>{w.name} — {w.code}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          {/* ч.863: «Підстава» — назва поля, документ під нею; кнопка — лише поки
              звʼязку немає (як у поверненні від покупця, ч.861). */}
          <label>{form.base_receipt_id ? 'Підстава *' : '\u00A0'}</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {!form.base_receipt_id && (
            <button type="button" className="btn-doc"
              onClick={() => setReceiptPickerOpen(true)}
              disabled={isPosted || !isNew || !form.contractor_id}
              title={!form.contractor_id
                  ? 'Спершу оберіть постачальника'
                  : 'Обрати проведений прихід: рядки заповняться залишком до повернення'}>
              <DocIcon />заповнити з приходу
            </button>
            )}
            {form.base_receipt_id && (() => {
              const basisRcp = receiptsQ.data?.items.find((x) => x.id === form.base_receipt_id);
              return (
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap',
                  display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <a style={{ cursor: 'pointer', color: 'var(--primary)' }}
                    onClick={() => navigate(`/goods-receipts/${form.base_receipt_id}`)}>
                    прихід{basisRcp
                      ? ` №${basisRcp.document_number} від ${new Date(basisRcp.document_date).toLocaleDateString('uk-UA')}`
                      : ''}
                  </a>
                  {isNew && !isPosted && (
                    <button type="button" className="btn-x"
                      onClick={() => setForm({ ...form, base_receipt_id: '', items: [] })}
                      title="Зняти звʼязок з приходом і очистити рядки">×</button>
                  )}
                </span>
              );
            })()}
          </div>
        </div>
      </div>

      </DocBlock>

      <DocBlock index={3} title="Товари">
      {/* Вкладки як у поверненні від покупця (рішення власника 2026-08-09). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <div className="seg-tabs">
          <button type="button" className={viewTab === 'lines' ? 'active' : ''}
            onClick={() => setViewTab('lines')}>Рядки ({form.items.length})</button>
        </div>
        <div className="seg-tabs" style={{ marginLeft: 'auto' }}>
          <button type="button" className={viewTab === 'accounts' ? 'active' : ''}
            onClick={() => setViewTab('accounts')}>Рахунки обліку</button>
          <button type="button" className={viewTab === 'tax' ? 'active' : ''}
            onClick={() => setViewTab('tax')}>Податковий облік</button>
        </div>
      </div>
      {viewTab === 'accounts' && (
        <div style={{ marginTop: '0.5rem' }}>
          <TchTable>
            <thead>
              <tr style={{ textTransform: 'none' }}>
                <th>Номенклатура</th>
                <th style={{ width: 200 }}
                    title="Рахунок запасів рядка — звідки списується товар при поверненні (порожньо — авто: 281)">
                  Рах. обліку (запаси)</th>
              </tr>
            </thead>
            <tbody>
              {form.items.map((it, i) => {
                const nomAcc = nomList.data?.items.find((n) => n.id === it.nomenclature_id);
                return (
                  <tr key={i}>
                    <td>{nomAcc?.name ?? '—'}</td>
                    <td>
                      <input value={it.account_code ?? ''} maxLength={10}
                        onChange={(e) => setForm((f) => ({ ...f,
                          items: f.items.map((row, idx) => idx === i
                            ? { ...row, account_code: e.target.value || null } : row) }))}
                        disabled={isPosted}
                        placeholder="авто: 281"
                        style={{ width: '100%' }} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TchTable>
          <p style={{ color: 'var(--muted)', fontSize: '0.78rem', marginTop: '0.4rem' }}>
            Порожнє поле — рахунок підбирається автоматично (типовий 281).
            Впишіть свій код, щоб перебити.
          </p>
        </div>
      )}
      {viewTab === 'tax' && (
        <TchTable style={{ marginTop: '0.5rem' }}>
          <thead>
            <tr style={{ textTransform: 'none' }}>
              <th>Номенклатура</th>
              <th style={{ width: 180 }}>Ставка ПДВ</th>
              <th style={{ width: 120, textAlign: 'right' }}>Сума ПДВ</th>
            </tr>
          </thead>
          <tbody>
            {form.items.map((it, i) => {
              const nomTax = nomList.data?.items.find((n) => n.id === it.nomenclature_id);
              return (
                <tr key={i}>
                  <td>{nomTax?.name ?? '—'}</td>
                  <td>
                    <select value={it.vat_rate_id} disabled={isPosted}
                      onChange={(e) => setRate(i, e.target.value)}>
                      {vatList.data?.map((v) => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {fmt(Number(it.vat_amount))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </TchTable>
      )}
      {viewTab === 'lines' && (
      <TchTable style={{ marginTop: '0.5rem' }}>
        <thead>
          <tr>
            <th style={{ width: 40 }}>#</th>
            <th>Номенклатура</th>
            <th style={{ width: 130 }}
                title="Кількість до повернення (максимум — залишок за приходом)">Кількість</th>
            <th style={{ width: 110 }}>Ціна</th>
            <th style={{ width: 120 }}>Сума без ПДВ</th>
            <th style={{ width: 110 }}>Сума ПДВ</th>
            <th style={{ width: 120 }}>Усього</th>
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.length === 0 && (
            <tr><td colSpan={8} style={{ color: 'var(--muted)' }}>
              Оберіть постачальника і натисніть «заповнити з приходу».
            </td></tr>
          )}
          {form.items.map((it, i) => {
            const nom = nomList.data?.items.find((n) => n.id === it.nomenclature_id);
            return (
              <tr key={i}>
                <td>{it.line_number}</td>
                <td>{nom?.name ?? '—'}</td>
                <td>
                  <input type="number" step="0.001" min={0} max={it.max_qty}
                         value={it.quantity}
                         onChange={(e) => setQty(i, e.target.value)}
                         disabled={isPosted} style={{ width: '100%' }} />
                  {it.max_qty != null && (
                    <small style={{ color: 'var(--muted)' }}>до {it.max_qty}</small>
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>{fmt(Number(it.price))}</td>
                <td style={{ textAlign: 'right' }}>{fmt(Number(it.amount_without_vat))}</td>
                <td style={{ textAlign: 'right' }}>{fmt(Number(it.vat_amount))}</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(Number(it.total_amount))}</td>
                <td>
                  {!isPosted && (
                    <button onClick={() => removeRow(i)} className="danger"
                            style={{ padding: '0.3rem 0.6rem' }}>×</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
        {form.items.length > 0 && (
          <tfoot>
            <tr>
              <td colSpan={4} style={{ textAlign: 'right' }}>Усього:</td>
              <td style={{ textAlign: 'right' }}>{fmt(totalNoVat)}</td>
              <td style={{ textAlign: 'right' }}>{fmt(totalVat)}</td>
              <td style={{ textAlign: 'right' }}>{fmt(total)}</td>
              <td></td>
            </tr>
          </tfoot>
        )}
      </TchTable>
      )}

      </DocBlock>

      <DocBlock index={4} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType={'doc.goods_returns_to_supplier'} documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div className="error">{error}</div>}

      {receiptPickerOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--overlay)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}
             onClick={() => setReceiptPickerOpen(false)}>
          <div className="card" style={{ width: 640, maxHeight: '70vh', overflowY: 'auto', margin: 0 }}
               onClick={(e) => e.stopPropagation()}>
            <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0 }}>Проведені приходи постачальника</h3>
              <button style={{ marginLeft: 'auto' }} onClick={() => setReceiptPickerOpen(false)}>закрити</button>
            </div>
            {(receiptsQ.data?.items.length ?? 0) === 0 && (
              <p style={{ color: 'var(--muted)' }}>Немає проведених приходів цього постачальника.</p>
            )}
            <table className="tch">
              <tbody>
                {receiptsQ.data?.items.map((rc) => (
                  <tr key={rc.id} onClick={() => void applyFromReceipt(rc.id)}
                      style={{ cursor: 'pointer', borderBottom: '1px solid var(--border-soft)' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-soft)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}>
                    <td>{rc.document_number}</td>
                    <td>
                      {new Date(rc.document_date).toLocaleDateString('uk-UA')}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {Number(rc.total_amount).toLocaleString('uk-UA', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {rkOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--overlay)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}
             onClick={() => setRkOpen(false)}>
          <div className="card" style={{ width: 520, margin: 0 }}
               onClick={(e) => e.stopPropagation()}>
            <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0 }}>РК до вхідної ПН</h3>
              <button style={{ marginLeft: 'auto' }} onClick={() => setRkOpen(false)}>закрити</button>
            </div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              РК до вхідної ПН — коригує підтверджений податковий кредит.
              Сума повернення: {fmt(total)} грн.
            </p>
            <div className="field">
              <label>ПН, що коригується *</label>
              <select value={rkInvoiceId} onChange={(e) => setRkInvoiceId(e.target.value)}>
                <option value="">— оберіть —</option>
                {invoicesQ.data?.items.map((inv) => (
                  <option key={inv.id} value={inv.id}>
                    №{inv.invoice_number} від {new Date(inv.invoice_date).toLocaleDateString('uk-UA')} — {fmt(Number(inv.total_amount))}
                  </option>
                ))}
              </select>
            </div>
            <div className="doc-grid">
              <div className="field" style={{ gridColumn: 'span 6' }}>
                <label>№ РК постачальника *</label>
                <input value={rkNumber} onChange={(e) => setRkNumber(e.target.value)} />
              </div>
              <div className="field" style={{ gridColumn: 'span 6' }}>
                <label>Дата РК</label>
                <DatePopover value={rkDate} onChange={setRkDate} />
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <input type="checkbox" checked={rkRegistered} style={{ width: 'auto' }}
                onChange={(e) => setRkRegistered(e.target.checked)} />
              зареєстрований в ЄРПН
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.75rem' }}>
              <button onClick={() => setRkOpen(false)}>скасувати</button>
              <button className="primary"
                disabled={!rkInvoiceId || !rkNumber || createRk.isPending}
                onClick={() => createRk.mutate()}>
                створити РК
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    const d = r?.data?.detail;
    if (typeof d === 'string') return d;
    return 'Сервер не зміг обробити запит.';
  }
  return String(e);
}
